import { useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { Alert, Box, Button, Flex, Text } from '@backstage/ui';

type NotificationSeverity = 'critical' | 'high' | 'normal' | 'low';

const severityLabels: Record<NotificationSeverity, string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  critical: 'Critical',
};

export function NotificationTestButtons() {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [sending, setSending] = useState<NotificationSeverity>();
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string>();

  const sendNotification = async (severity: NotificationSeverity) => {
    setSending(severity);
    setMessage('');
    setError(undefined);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('notifications');
      const response = await fetchApi.fetch(`${baseUrl}/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ severity }),
      });
      if (!response.ok) {
        throw new Error(`Failed to send notification: ${response.statusText}`);
      }
      setMessage(
        `${severityLabels[severity]} notification sent to core-portal.`,
      );
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : 'Failed to send notification',
      );
    } finally {
      setSending(undefined);
    }
  };

  return (
    <Box mt="4" p="4">
      <Flex direction="column" gap="3">
        <Text as="h2" variant="title-small">
          Test notifications
        </Text>
        <Text as="p">
          Send a sample group-created notification to group:default/core-portal.
          This does not create a group. Configured email and OS delivery may
          run; only recipients will receive the notification.
        </Text>
        <Flex gap="3" style={{ flexWrap: 'wrap' }}>
          {(
            ['low', 'normal', 'high', 'critical'] as NotificationSeverity[]
          ).map(severity => (
            <Button
              key={severity}
              variant="secondary"
              isDisabled={sending !== undefined}
              isPending={sending === severity}
              onPress={() => void sendNotification(severity)}
            >
              {severityLabels[severity]}
            </Button>
          ))}
        </Flex>
        <Text as="p" role="status">
          {message}
        </Text>
        {error && <Alert role="alert" status="danger" title={error} />}
      </Flex>
    </Box>
  );
}
